/**
 * DemoAuthProvider.tsx — Demo Mode Auth Bypass
 *
 * When VITE_DEMO_MODE=true this component replaces the normal Firebase auth
 * listener (AuthInitializer in App.tsx) by directly populating the Zustand
 * auth store with a synthetic Firebase User-shaped object.
 *
 * Strategy:
 *   - On mount, immediately call setUser with a fake Firebase User object so
 *     the app treats the demo donor as authenticated.
 *   - On unmount, call reset() to clean up (important during hot-module
 *     replacement in development).
 *   - The fake user object implements the Firebase User interface enough to
 *     satisfy the app's auth store and any component that reads user.uid,
 *     user.email, user.displayName, etc.
 *   - getIdToken always resolves to a fake JWT string so the Axios interceptor
 *     does not call Firebase; the demo token is never sent to a real backend.
 *
 * The component renders a visible "Demo Mode" banner so it is always clear
 * during the presentation that this is not production data.
 *
 * Usage (in main.tsx):
 *   Replace <AuthInitializer /> with <DemoAuthProvider /> inside the provider
 *   tree when VITE_DEMO_MODE === 'true'.
 */
import { useEffect } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import type { UserRole } from '@philofund/shared/types/user';
import { useAuthStore } from '@/stores/auth.store';
import { DEMO_USERS_BY_ROLE } from '@/mocks/seed-data';
import { DemoRoleSwitcher } from '@/mocks/DemoRoleSwitcher';
import { isDemoGuest, readDemoRole } from '@/demo/demo-role';

// ---------------------------------------------------------------------------
// Fake Firebase User
// ---------------------------------------------------------------------------

/**
 * Builds a minimal object that satisfies the Firebase User interface well
 * enough for the Zustand auth store and all app components.
 *
 * We use `as unknown as FirebaseUser` because the full Firebase User has ~40
 * properties we don't need to implement for the demo. Any component that
 * reaches for a property not listed here will get `undefined` — acceptable
 * since the demo only exercises the listed paths.
 *
 * The selected demo `role` is attached as a custom `appRole` property — the
 * exact property `ProtectedRoute` reads to gate role-restricted routes (e.g.
 * `/admin` requires `platform_admin`). The identity is the ROLE'S persona
 * (Alex Morgan · donor, Jordan Reyes · org admin, Sam Diaz · PhiloFund
 * administration) so each role sees its own name everywhere.
 */
function makeFakeUser(role: UserRole): FirebaseUser {
  const persona = DEMO_USERS_BY_ROLE[role];
  return {
    uid: persona.id,
    email: persona.email,
    displayName: persona.displayName,
    photoURL: persona.photoUrl ?? null,
    emailVerified: persona.emailVerified,
    isAnonymous: false,
    phoneNumber: null,
    providerId: 'password',
    providerData: [],
    refreshToken: 'demo-refresh-token',
    tenantId: null,
    // Custom claim consumed by ProtectedRoute's getUserRole().
    appRole: role,
    metadata: {
      creationTime: persona.createdAt,
      lastSignInTime: persona.updatedAt,
    },
    /**
     * Returns a fake JWT. The Axios interceptor attaches it as the Bearer
     * token, but MSW intercepts all requests before they leave the browser so
     * no real network call ever uses this value.
     */
    getIdToken: async (_forceRefresh?: boolean): Promise<string> => {
      return 'demo.jwt.token';
    },
    getIdTokenResult: async (_forceRefresh?: boolean) => ({
      token: 'demo.jwt.token',
      authTime: persona.createdAt,
      issuedAtTime: persona.createdAt,
      expirationTime: '2099-12-31T23:59:59.000Z',
      signInProvider: 'password',
      signInSecondFactor: null,
      claims: { role },
    }),
    reload: async (): Promise<void> => {
      // no-op
    },
    delete: async (): Promise<void> => {
      throw new Error('Cannot delete user in demo mode.');
    },
    toJSON: (): object => ({
      uid: persona.id,
      email: persona.email,
    }),
  } as unknown as FirebaseUser;
}

// ---------------------------------------------------------------------------
// DemoAuthProvider component
// ---------------------------------------------------------------------------

/**
 * Drop-in replacement for AuthInitializer during demo mode.
 *
 * As a side effect it:
 *   1. Immediately authenticates the demo user (with the selected appRole) in
 *      the Zustand store.
 *   2. Cleans up on unmount.
 *
 * It renders the fixed "DEMO MODE" banner plus the DemoRoleSwitcher, so both
 * are visible on every page of the application without any change to App.tsx.
 */
export function DemoAuthProvider(): JSX.Element {
  const { setUser, reset } = useAuthStore();

  useEffect(() => {
    // Immediately mark auth as resolved with the fake user, minting its
    // appRole from the persisted demo role (default 'donor') — or with no
    // user at all for the signed-out visitor view.
    // setUser also sets isLoading=false which unblocks route rendering.
    setUser(isDemoGuest() ? null : makeFakeUser(readDemoRole()));

    return () => {
      reset();
    };
  }, [setUser, reset]);

  return (
    <>
      <DemoRoleSwitcher />
      <DemoModeBanner />
    </>
  );
}

// ---------------------------------------------------------------------------
// Demo Mode Banner
// ---------------------------------------------------------------------------

/**
 * A highly visible fixed banner reminding the audience that demo mode is
 * active. Uses inline styles intentionally — no MUI dependency here keeps
 * the demo infrastructure self-contained and ensures it renders even if
 * the theme fails to load.
 */
function DemoModeBanner(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Demo mode is active — no real data or transactions"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: '#FF6F00',
        color: '#ffffff',
        textAlign: 'center',
        padding: '6px 16px',
        fontSize: '13px',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 600,
        letterSpacing: '0.04em',
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    >
      DEMO MODE — All data is simulated. No real donations or accounts.
    </div>
  );
}
